// Carriers screen — Phase 4 Plan 04-04 Task 2 (FWD-04, FWD-05, D-72); migrated
// to telegram-ui in Phase 12 Plan 12-08 (UIR-07, UI-SPEC §7 Carriers).
//
// Owner-only Carriers CRM with:
//   1. List view: search input + carrier cells (Avatar + rating stars + contacts
//      + Registered/Pending pill + [Assign to trip] + [Edit] + [Delete]).
//      Empty → Placeholder.
//   2. AddCarrierModal: form with name*, email, telegram (@-strip), phone,
//      rating (interactive RatingStar), notes; submits to POST /carriers.
//   3. EditCarrierModal: same form prefilled.
//   4. AssignToTripSheet: lists active trips from GET /trips?status=active,
//      tap to assign → toast (immediate) or pending invite deep-link share.
//
// Role gate (UX-only; server enforces require_role(["owner"])): if JWT role
// !== "owner", show owner-required gate. (Security V4 — gating must not regress
// through the migration.) JWT decode consolidated onto lib/jwt.ts (12-08).
//
// Sources:
//   - 04-UI-SPEC §Screen 9 (Carriers wireframe + AddModal + Assign sheet)
//   - 12-UI-SPEC §7 Carriers (List+Cell+Avatar+RatingStar; empty → Placeholder)

import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  Avatar,
  Badge,
  Button,
  Cell,
  Input,
  List,
  Placeholder,
  Section,
  Textarea,
} from "@telegram-apps/telegram-ui";
import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";

import {
  type AssignmentResponse,
  type CarrierCreateBody,
  type CarrierListResponse,
  type CarrierResponse,
  carriersApi,
} from "../api/carriers";
import { type TripListItem, tripsApi } from "../api/trips";
import { useAuth } from "../auth/useAuth";
import { RatingStar } from "../components/RatingStar";
import { decodeJwtPayload } from "../lib/jwt";
import { SPACING } from "../lib/tokens";

// --------------------------------------------------------------------------- //
// Role gate (consolidated onto lib/jwt.ts)
// --------------------------------------------------------------------------- //

function useUserRole(): { role: string | null } {
  const { jwt } = useAuth();
  return useMemo(() => {
    const payload = decodeJwtPayload(jwt);
    return { role: payload?.role ?? null };
  }, [jwt]);
}

function acronymFor(name: string): string {
  if (!name || name.trim() === "") return "?";
  const parts = name.trim().split(/\s+/);
  const first = parts[0]?.[0] ?? "";
  const second = parts.length > 1 ? (parts[parts.length - 1]?.[0] ?? "") : "";
  return (first + second).toUpperCase() || "?";
}

// --------------------------------------------------------------------------- //
// Modal state machines
// --------------------------------------------------------------------------- //

type AddModalState =
  | { kind: "closed" }
  | { kind: "open"; mode: "create" }
  | { kind: "open"; mode: "edit"; carrier: CarrierResponse };

type AssignModalState =
  | { kind: "closed" }
  | { kind: "open"; carrier: CarrierResponse }
  | { kind: "result"; assignment: AssignmentResponse };

// --------------------------------------------------------------------------- //
// Telegram regex client-side check (mirror schema validator)
// --------------------------------------------------------------------------- //

const TELEGRAM_RE = /^[A-Za-z0-9_]{5,32}$/;

function stripAt(value: string): string {
  return value.startsWith("@") ? value.slice(1) : value;
}

function validateTelegramOrNull(raw: string): string | null | "invalid" {
  const trimmed = stripAt(raw.trim());
  if (trimmed === "") return null;
  return TELEGRAM_RE.test(trimmed) ? trimmed : "invalid";
}

// --------------------------------------------------------------------------- //
// Carriers screen
// --------------------------------------------------------------------------- //

export function Carriers(): React.JSX.Element {
  const { t } = useTranslation();
  const { apiClient } = useAuth();
  const queryClient = useQueryClient();
  const { role } = useUserRole();
  const isOwner = role === "owner";

  const [search, setSearch] = useState<string>("");
  const [addModal, setAddModal] = useState<AddModalState>({ kind: "closed" });
  const [assignModal, setAssignModal] = useState<AssignModalState>({
    kind: "closed",
  });
  const [toast, setToast] = useState<string | null>(null);

  const listQuery = useQuery<CarrierListResponse>({
    queryKey: ["carriers", search],
    queryFn: () =>
      carriersApi.list(apiClient, {
        search: search.trim() || undefined,
        limit: 50,
      }),
    enabled: isOwner,
  });

  const createMutation = useMutation({
    mutationFn: (body: CarrierCreateBody) => carriersApi.create(apiClient, body),
    onSuccess: () => {
      setAddModal({ kind: "closed" });
      setToast(t("dispatcher:carriers.toast.created", "Carrier saved."));
      queryClient.invalidateQueries({ queryKey: ["carriers"] });
    },
  });

  const updateMutation = useMutation({
    mutationFn: (args: { id: number; body: CarrierCreateBody }) =>
      carriersApi.update(apiClient, args.id, args.body),
    onSuccess: () => {
      setAddModal({ kind: "closed" });
      setToast(t("dispatcher:carriers.toast.updated", "Carrier updated."));
      queryClient.invalidateQueries({ queryKey: ["carriers"] });
    },
  });

  const deleteMutation = useMutation({
    mutationFn: (id: number) => carriersApi.delete(apiClient, id),
    onSuccess: () => {
      setToast(t("dispatcher:carriers.toast.deleted", "Carrier deleted."));
      queryClient.invalidateQueries({ queryKey: ["carriers"] });
    },
  });

  const assignMutation = useMutation({
    mutationFn: (args: { carrierId: number; tripId: number }) =>
      carriersApi.assignTrip(apiClient, args.carrierId, args.tripId),
    onSuccess: (assignment) => {
      setAssignModal({ kind: "result", assignment });
      queryClient.invalidateQueries({ queryKey: ["carriers"] });
    },
  });

  // Owner-only gate (Security V4 — must not regress).
  if (!isOwner) {
    return (
      <List data-testid="carriers-owner-gate">
        <Placeholder
          header={t("dispatcher:carriers.title", "Carriers") as string}
          description={
            t(
              "dispatcher:carriers.owner_required",
              "Owner access required. Contact the company owner to manage carriers.",
            ) as string
          }
        />
      </List>
    );
  }

  const items = listQuery.data?.items ?? [];

  return (
    <List data-testid="carriers" style={{ paddingBottom: 80 }}>
      <Input
        type="search"
        data-testid="carriers-search"
        placeholder={t("dispatcher:carriers.search.placeholder", "Search carrier name") as string}
        value={search}
        onChange={(e) => setSearch(e.target.value)}
      />

      {listQuery.isLoading && (
        <p data-testid="carriers-loading">{t("dispatcher:carriers.loading", "Loading…")}</p>
      )}

      {listQuery.data && items.length === 0 && (
        <Placeholder
          data-testid="carriers-empty"
          header={t("dispatcher:carriers.empty.title", "No carriers yet") as string}
          description={
            t(
              "dispatcher:carriers.empty.body",
              "Add carrier contacts to assign trips to external transporters.",
            ) as string
          }
        />
      )}

      {items.length > 0 && (
        <Section data-testid="carriers-list">
          {items.map((c) => (
            <CarrierCell
              key={c.id}
              carrier={c}
              onEdit={() => setAddModal({ kind: "open", mode: "edit", carrier: c })}
              onAssign={() => setAssignModal({ kind: "open", carrier: c })}
              onDelete={() => {
                if (
                  typeof window !== "undefined" &&
                  window.confirm(
                    t(
                      "dispatcher:carriers.delete_confirm",
                      "Delete this carrier? Past trip assignments are preserved.",
                    ),
                  )
                ) {
                  deleteMutation.mutate(c.id);
                }
              }}
            />
          ))}
        </Section>
      )}

      <div
        style={{
          position: "fixed",
          bottom: 0,
          left: 0,
          right: 0,
          padding: SPACING.md,
          background: "var(--tgui--bg_color, white)",
          borderTop: "1px solid var(--tgui--hint_color, #E2E8F0)",
        }}
      >
        <Button
          size="l"
          stretched
          data-testid="carriers-add-cta"
          onClick={() => setAddModal({ kind: "open", mode: "create" })}
        >
          {t("dispatcher:carriers.add_cta", "+ Add carrier")}
        </Button>
      </div>

      {addModal.kind === "open" && (
        <AddCarrierModal
          mode={addModal.mode}
          existing={addModal.mode === "edit" ? addModal.carrier : null}
          onCancel={() => setAddModal({ kind: "closed" })}
          onSubmit={(body) => {
            if (addModal.mode === "edit") {
              updateMutation.mutate({ id: addModal.carrier.id, body });
            } else {
              createMutation.mutate(body);
            }
          }}
          submitting={createMutation.isPending || updateMutation.isPending}
        />
      )}

      {assignModal.kind !== "closed" && (
        <AssignToTripSheet
          state={assignModal}
          onClose={() => setAssignModal({ kind: "closed" })}
          onPick={(tripId, carrierId) => assignMutation.mutate({ carrierId, tripId })}
          submitting={assignMutation.isPending}
        />
      )}

      {toast && (
        <div
          data-testid="carriers-toast"
          role="status"
          aria-live="polite"
          onClick={() => setToast(null)}
          style={{
            position: "fixed",
            bottom: 80,
            left: SPACING.md,
            right: SPACING.md,
            padding: SPACING.md,
            background: "var(--tgui--secondary_bg_color, #eef)",
            border: "1px solid var(--tgui--hint_color, #E2E8F0)",
            borderRadius: 8,
            cursor: "pointer",
          }}
        >
          {toast}
        </div>
      )}
    </List>
  );
}

// --------------------------------------------------------------------------- //
// Carrier cell component
// --------------------------------------------------------------------------- //

interface CarrierCellProps {
  carrier: CarrierResponse;
  onEdit: () => void;
  onAssign: () => void;
  onDelete: () => void;
}

function CarrierCell({ carrier, onEdit, onAssign, onDelete }: CarrierCellProps): React.JSX.Element {
  const { t } = useTranslation();
  const isRegistered = carrier.linked_company_id !== null;

  return (
    <Cell
      data-testid={`carrier-row-${carrier.id}`}
      multiline
      before={
        <Avatar
          size={40}
          acronym={acronymFor(carrier.carrier_name)}
          data-testid={`carrier-avatar-${carrier.id}`}
        />
      }
      subtitle={
        <span>
          {carrier.contact_telegram && <span>@{carrier.contact_telegram} · </span>}
          {carrier.contact_phone && <span>{carrier.contact_phone}</span>}
        </span>
      }
      titleBadge={<Badge type="dot" mode={isRegistered ? "primary" : "gray"} />}
    >
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
        <strong>{carrier.carrier_name}</strong>
        <RatingStar value={carrier.rating} readOnly size={16} />
      </div>
      <div style={{ marginTop: 6, fontSize: 12 }}>
        <span data-testid={`carrier-status-${carrier.id}`}>
          {isRegistered
            ? t("dispatcher:carriers.status.registered", "Registered")
            : t("dispatcher:carriers.status.pending", "Invited (pending)")}
        </span>
      </div>
      <div style={{ marginTop: 10, display: "flex", gap: 8 }}>
        <Button
          size="s"
          mode="bezeled"
          data-testid={`carrier-assign-${carrier.id}`}
          onClick={onAssign}
          style={{ flex: 1 }}
        >
          {t("dispatcher:carriers.assign_cta", "Assign to trip")}
        </Button>
        <Button size="s" mode="outline" data-testid={`carrier-edit-${carrier.id}`} onClick={onEdit}>
          {t("dispatcher:carriers.edit_cta", "Edit")}
        </Button>
        <Button
          size="s"
          mode="plain"
          data-testid={`carrier-delete-${carrier.id}`}
          onClick={onDelete}
          aria-label={t("dispatcher:carriers.delete_cta", "Delete") as string}
          style={{ color: "var(--tgui--destructive_text_color, #dc2626)" }}
        >
          ✕
        </Button>
      </div>
    </Cell>
  );
}

// --------------------------------------------------------------------------- //
// AddCarrierModal
// --------------------------------------------------------------------------- //

interface AddCarrierModalProps {
  mode: "create" | "edit";
  existing: CarrierResponse | null;
  onCancel: () => void;
  onSubmit: (body: CarrierCreateBody) => void;
  submitting: boolean;
}

function AddCarrierModal({
  mode,
  existing,
  onCancel,
  onSubmit,
  submitting,
}: AddCarrierModalProps): React.JSX.Element {
  const { t } = useTranslation();
  const [name, setName] = useState<string>(existing?.carrier_name ?? "");
  const [email, setEmail] = useState<string>(existing?.contact_email ?? "");
  const [telegram, setTelegram] = useState<string>(existing?.contact_telegram ?? "");
  const [phone, setPhone] = useState<string>(existing?.contact_phone ?? "");
  const [rating, setRating] = useState<number | null>(existing?.rating ?? null);
  const [notes, setNotes] = useState<string>(existing?.notes ?? "");
  const [error, setError] = useState<string | null>(null);

  const handleSubmit = (e: React.FormEvent): void => {
    e.preventDefault();
    setError(null);

    if (!name.trim()) {
      setError(t("dispatcher:carriers.error.name_required", "Name is required"));
      return;
    }
    const tgCheck = validateTelegramOrNull(telegram);
    if (tgCheck === "invalid") {
      setError(
        t(
          "dispatcher:carriers.error.telegram_invalid",
          "Telegram @username must be 5-32 chars: letters, digits, underscore.",
        ),
      );
      return;
    }
    onSubmit({
      carrier_name: name.trim(),
      contact_email: email.trim() || null,
      contact_telegram: tgCheck,
      contact_phone: phone.trim() || null,
      rating,
      notes: notes.trim() || null,
    });
  };

  return (
    <div
      data-testid="carrier-add-modal"
      role="dialog"
      aria-modal="true"
      style={{
        position: "fixed",
        inset: 0,
        background: "rgba(0,0,0,0.4)",
        display: "flex",
        alignItems: "flex-end",
        zIndex: 100,
      }}
    >
      <form
        onSubmit={handleSubmit}
        style={{
          width: "100%",
          background: "var(--tgui--bg_color, #fff)",
          borderTopLeftRadius: 16,
          borderTopRightRadius: 16,
          maxHeight: "85vh",
          overflowY: "auto",
        }}
      >
        <Section
          header={
            mode === "edit"
              ? (t("dispatcher:carriers.edit_title", "Edit carrier") as string)
              : (t("dispatcher:carriers.add_title", "Add carrier") as string)
          }
        >
          <Input
            data-testid="carrier-form-name"
            header={`${t("dispatcher:carriers.add.name", "Name")} *`}
            value={name}
            onChange={(e) => setName(e.target.value)}
            required
          />
          <Input
            type="email"
            data-testid="carrier-form-email"
            header={t("dispatcher:carriers.add.email", "Contact email") as string}
            value={email}
            onChange={(e) => setEmail(e.target.value)}
          />
          <Input
            data-testid="carrier-form-telegram"
            header={t("dispatcher:carriers.add.telegram", "Telegram @") as string}
            value={telegram}
            onChange={(e) => setTelegram(e.target.value)}
            placeholder="username"
          />
          <Input
            data-testid="carrier-form-phone"
            header={t("dispatcher:carriers.add.phone", "Phone") as string}
            value={phone}
            onChange={(e) => setPhone(e.target.value)}
          />
          <Cell
            multiline
            subhead={t("dispatcher:carriers.add.rating", "Rating") as string}
            interactiveAnimation="opacity"
          >
            <RatingStar value={rating} onChange={setRating} size={28} />
          </Cell>
          <Textarea
            data-testid="carrier-form-notes"
            header={t("dispatcher:carriers.add.notes", "Notes") as string}
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            rows={3}
          />
        </Section>

        {error && (
          <p
            data-testid="carrier-form-error"
            style={{
              color: "var(--tgui--destructive_text_color, #dc2626)",
              padding: `0 ${SPACING.md}px`,
            }}
          >
            {error}
          </p>
        )}

        <div style={{ display: "flex", gap: SPACING.sm, padding: SPACING.md }}>
          <Button size="l" mode="plain" stretched type="button" onClick={onCancel}>
            {t("dispatcher:carriers.cancel_cta", "Cancel")}
          </Button>
          <Button
            size="l"
            stretched
            type="submit"
            data-testid="carrier-form-submit"
            disabled={submitting}
          >
            {submitting
              ? t("dispatcher:carriers.saving", "Saving…")
              : t("dispatcher:carriers.save_cta", "Save carrier")}
          </Button>
        </div>
      </form>
    </div>
  );
}

// --------------------------------------------------------------------------- //
// AssignToTripSheet
// --------------------------------------------------------------------------- //

interface AssignToTripSheetProps {
  state: Exclude<AssignModalState, { kind: "closed" }>;
  onClose: () => void;
  onPick: (tripId: number, carrierId: number) => void;
  submitting: boolean;
}

function AssignToTripSheet({
  state,
  onClose,
  onPick,
  submitting,
}: AssignToTripSheetProps): React.JSX.Element {
  const { t } = useTranslation();
  const { apiClient } = useAuth();

  const tripsQuery = useQuery({
    queryKey: ["trips-active-for-assignment"],
    queryFn: () => tripsApi.list(apiClient, { status: "active", limit: 50 }),
    enabled: state.kind === "open",
  });

  return (
    <div
      data-testid="carrier-assign-sheet"
      role="dialog"
      aria-modal="true"
      style={{
        position: "fixed",
        inset: 0,
        background: "rgba(0,0,0,0.4)",
        display: "flex",
        alignItems: "flex-end",
        zIndex: 100,
      }}
    >
      <div
        style={{
          width: "100%",
          background: "var(--tgui--bg_color, #fff)",
          padding: 16,
          borderTopLeftRadius: 16,
          borderTopRightRadius: 16,
          maxHeight: "85vh",
          overflowY: "auto",
        }}
      >
        <h3>{t("dispatcher:carriers.assign_title", "Assign trip")}</h3>

        {state.kind === "open" && (
          <>
            <p style={{ fontSize: 13 }}>
              {t("dispatcher:carriers.assign_hint", "Pick an active trip to assign to {{name}}.", {
                name: state.carrier.carrier_name,
              })}
            </p>
            {tripsQuery.isLoading && (
              <p data-testid="assign-trips-loading">
                {t("dispatcher:carriers.assign_loading", "Loading trips…")}
              </p>
            )}
            {tripsQuery.data && tripsQuery.data.items.length === 0 && (
              <p data-testid="assign-trips-empty">
                {t("dispatcher:carriers.assign_empty", "No active trips available.")}
              </p>
            )}
            {tripsQuery.data?.items.map((trip: TripListItem) => (
              <Cell
                key={trip.id}
                Component="button"
                data-testid={`assign-trip-pick-${trip.id}`}
                disabled={submitting}
                onClick={() => onPick(trip.id, state.carrier.id)}
                subtitle={trip.status}
              >
                #{trip.external_id ?? trip.id}
              </Cell>
            ))}
          </>
        )}

        {state.kind === "result" && (
          <div data-testid="carrier-assign-result">
            {state.assignment.immediate ? (
              <p>
                {t(
                  "dispatcher:carriers.assign_toast_immediate",
                  "Trip #{{tripId}} assigned to {{carrier}}.",
                  {
                    tripId: state.assignment.trip_id,
                    carrier: state.assignment.carrier_name,
                  },
                )}
              </p>
            ) : (
              <>
                <p>
                  {t(
                    "dispatcher:carriers.assign_toast_invite",
                    "Invitation sent. Share this link with {{carrier}}:",
                    { carrier: state.assignment.carrier_name },
                  )}
                </p>
                <code
                  data-testid="carrier-assign-deep-link"
                  style={{
                    display: "block",
                    padding: SPACING.sm,
                    background: "var(--tgui--secondary_bg_color, #f3f4f6)",
                    wordBreak: "break-all",
                    fontSize: 13,
                  }}
                >
                  {state.assignment.invite_deep_link}
                </code>
              </>
            )}
          </div>
        )}

        <div style={{ marginTop: 12 }}>
          <Button size="l" stretched type="button" onClick={onClose}>
            {t("dispatcher:carriers.assign_close", "Close")}
          </Button>
        </div>
      </div>
    </div>
  );
}
